import toast from "react-hot-toast";
import { getArtifact, isWhitespace, isWordChar } from "@/services/multi-agent";
import { useMultiAgentIdeStore } from "./store/multiAgentIdeStore";
import { bareClassName } from "./utils/bareClassName";
import {
  getMetadataItemByName,
  artifactTypeToMetadataType,
  type ResolvedMetadataItemFull,
} from "./panels/resolveDependencyItemForEditor";
import type { AcceptedCodeOperation } from "./store/multiAgentIdeStore";

// ---------------------------------------------------------------------------
// Apex declaration parsing.
//
// We tokenize the source into maximal word-char runs (the runs delimit identifier
// boundaries) and require the separators between keywords to be non-empty
// all-whitespace gaps. This recognizes:
//   <access> [with sharing | without sharing | virtual | abstract]* <class|interface|enum> <name>
//   trigger <name> on ...
// ---------------------------------------------------------------------------
const APEX_ACCESS_MODIFIERS = new Set(["public", "private", "global"]);
const APEX_TYPE_KEYWORDS = new Set(["class", "interface", "enum"]);

interface WordToken {
  word: string;
  start: number;
  end: number;
}

function tokenizeWords(code: string): WordToken[] {
  const tokens: WordToken[] = [];
  let i = 0;
  while (i < code.length) {
    if (!isWordChar(code[i])) {
      i++;
      continue;
    }
    const start = i;
    while (i < code.length && isWordChar(code[i])) i++;
    tokens.push({ word: code.slice(start, i), start, end: i });
  }
  return tokens;
}

/** Non-empty all-whitespace gap between two adjacent tokens. */
function whitespaceBetween(code: string, a: WordToken, b: WordToken): boolean {
  if (b.start <= a.end) return false;
  for (let k = a.end; k < b.start; k++) {
    if (!isWhitespace(code[k])) return false;
  }
  return true;
}

/** At least one whitespace char immediately after a token. */
function whitespaceAfter(code: string, token: WordToken): boolean {
  return token.end < code.length && isWhitespace(code[token.end]);
}

function extractApexTypeName(code: string): string | undefined {
  const tokens = tokenizeWords(code);
  for (let i = 0; i < tokens.length; i++) {
    if (!APEX_ACCESS_MODIFIERS.has(tokens[i].word)) continue;
    let j = i + 1;
    // access modifier, then whitespace
    if (j >= tokens.length || !whitespaceBetween(code, tokens[i], tokens[j])) continue;
    // optional sharing/virtual/abstract modifiers, each followed by whitespace
    for (;;) {
      const w = tokens[j]?.word;
      if (w === "with" || w === "without") {
        const sharing = tokens[j + 1];
        const after = tokens[j + 2];
        if (
          sharing?.word === "sharing" &&
          after &&
          whitespaceBetween(code, tokens[j], sharing) &&
          whitespaceBetween(code, sharing, after)
        ) {
          j += 2;
          continue;
        }
        break;
      }
      if (w === "virtual" || w === "abstract") {
        if (tokens[j + 1] && whitespaceBetween(code, tokens[j], tokens[j + 1])) {
          j += 1;
          continue;
        }
        break;
      }
      break;
    }
    // class/interface/enum keyword, then whitespace, then the type name
    const keyword = tokens[j];
    if (!keyword || !APEX_TYPE_KEYWORDS.has(keyword.word)) continue;
    const name = tokens[j + 1];
    if (name && whitespaceBetween(code, keyword, name)) return name.word;
  }
  return undefined;
}

function extractTriggerName(code: string): string | undefined {
  const tokens = tokenizeWords(code);
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].word !== "trigger") continue;
    const name = tokens[i + 1];
    const on = tokens[i + 2];
    // trigger <name> on  (whitespace between each, and trailing whitespace after "on")
    if (
      name &&
      on &&
      on.word === "on" &&
      whitespaceBetween(code, tokens[i], name) &&
      whitespaceBetween(code, name, on) &&
      whitespaceAfter(code, on)
    ) {
      return name.word;
    }
  }
  return undefined;
}

export function extractApexName(code: string): string | undefined {
  return extractApexTypeName(code) ?? extractTriggerName(code);
}

export function inferMetadataType(name: string, code: string, fallback = "ApexClass"): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".trigger") || extractTriggerName(code) !== undefined) return "ApexTrigger";
  if (lower.endsWith(".page")) return "ApexPage";
  if (lower.endsWith(".js")) return "LightningComponentBundle";
  return fallback || "ApexClass";
}

/** Collapse runs of characters outside letters/digits/"_"/"."/"-" into a single "_". */
function sanitizeVirtualIdName(name: string): string {
  let out = "";
  let inDisallowedRun = false;
  for (const c of name) {
    const allowed =
      (c >= "a" && c <= "z") ||
      (c >= "A" && c <= "Z") ||
      (c >= "0" && c <= "9") ||
      c === "_" ||
      c === "." ||
      c === "-";
    if (allowed) {
      out += c;
      inDisallowedRun = false;
    } else if (!inDisallowedRun) {
      out += "_";
      inDisallowedRun = true;
    }
  }
  return out;
}

function safeVirtualId(metadataType: string, name: string): string {
  return `virtual:${metadataType}:${sanitizeVirtualIdName(name)}`;
}

/**
 * Resolve the org target for Compare — ID-FIRST, the dev-agent `resolveOrgTarget`
 * pattern. Prefer the emitted `source_artifact_id` (an `art_…` artifact id or a
 * metadata UUID) resolved via `getMetadataItem` (which routes on the id shape);
 * only when no id exists do we hit the AUTHORITATIVE by-name endpoint
 * (`/api/chat/metadata/items/by-name`) — never a fuzzy client-side search.
 */
async function resolveOrgTarget(
  sourceArtifactId: string | null | undefined,
  name: string,
): Promise<ResolvedMetadataItemFull | null> {
  try {
    if (sourceArtifactId?.trim()) {
      // source_artifact_id is a multi-agent artifact UUID — resolve it through
      // the org/user-scoped multi-agent artifact endpoint, never the dev-agent proxy.
      const art = await getArtifact(sourceArtifactId.trim());
      return {
        id: art.id || sourceArtifactId.trim(),
        name: art.name,
        metadata_type: artifactTypeToMetadataType(art.type || ""),
        content: art.content || "",
        language: art.language || undefined,
      };
    }
    if (name.trim()) {
      return await getMetadataItemByName(name.trim(), undefined, true);
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Open generated code in the workspace editor — ID-DRIVEN, mirroring dev-agent.
 *
 * Compare-vs-Create is decided by whether an org target resolves: if the patch
 * carries an inline/emitted `original`, OR `resolveOrgTarget` finds the existing
 * component (id-first), it's an edit → Compare. Otherwise → Create New. The real
 * metadata id flows through as the editor file id so save-back resolves by id.
 */
export async function openGeneratedCodeInMultiAgentWorkspace({
  code,
  name,
  metadataType,
  mode,
  original,
  sourceArtifactId,
  operation,
}: {
  code: string;
  name?: string;
  metadataType?: string;
  mode: "workspace" | "compare";
  original?: string | null;
  sourceArtifactId?: string | null;
  /** created/modified/deleted — a delete has `code === ""` BY DESIGN (no new
   *  body); pass it explicitly so the empty string is never mistaken for
   *  "nothing generated" downstream (showDiff, handleSave). */
  operation?: AcceptedCodeOperation;
}): Promise<void> {
  const isDelete = operation === "deleted";
  const codeName =
    (isDelete ? "" : extractApexName(code)) ||
    (name ? bareClassName(name) : "") ||
    "GeneratedApex";
  const resolvedType = inferMetadataType(name || codeName, code, metadataType || "ApexClass");
  // If the patch already carries the original (inline or fetched from
  // source_artifact_id), use it directly; otherwise resolve the org target BY ID
  // (dev-agent style) to get the baseline + the real metadata id for save-back.
  const existing = original?.trim()
    ? null
    : await resolveOrgTarget(sourceArtifactId, name || codeName);
  const id = sourceArtifactId?.trim() || existing?.id || safeVirtualId(resolvedType, codeName);
  const baseline = original?.trim() || existing?.content || "";
  // A delete always compares (there is no "create" reading for it — nothing to
  // create, only something to remove), independent of whether `code` is empty.
  const compare = isDelete || (mode === "compare" && baseline.trim().length > 0);

  useMultiAgentIdeStore.getState().setWorkspaceCodeRequest({
    id,
    name: existing?.name || name || codeName,
    metadataType: existing?.metadata_type || resolvedType,
    content: compare ? baseline : code,
    badge: compare ? "R" : "C",
    acceptedCode: compare ? code : null,
    acceptedCodeOriginal: compare ? baseline : null,
    acceptedCodeOperation: compare ? (operation ?? (baseline ? "modified" : "created")) : null,
  });

  toast.success(
    isDelete
      ? `Reviewing delete of ${existing?.name || name || codeName}`
      : compare
        ? `Comparing ${existing?.name || name || codeName}`
        : `Opened ${existing?.name || name || codeName} in workspace`,
  );
}
